import api from './api';
import { AUTH_TOKEN_KEY, AUTH_USER_KEY, AuthUser } from './authService';
import { t } from '../i18n';

export interface UserAddress {
  label?: string | null;
  city?: string | null;
  postcode?: string | null;
  street?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  country?: string | null;
  query?: string | null;
  [key: string]: any;
}

export interface PersonalData {
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  phone?: string | null;
  country?: string | null;
  address?: UserAddress | null;
  [key: string]: any;
}

export interface DartcloudUser {
  id?: string | null;
  _id?: string | null;
  username?: string | null;
  chatUsername?: string | null;
  role?: string | null;
  verified?: boolean | null;
  deactivated?: boolean | null;
  language?: string | null;
  profileImage?: string | null;
  notificationDistance?: number | null;
  notificationsEnabled?: boolean | null;
  notificationsEnabledFavourites?: boolean | null;
  notificationsEnabledNews?: boolean | null;
  notificationsEnabledOwner?: boolean | null;
  // ADDITIV (2026-09-03): Community-Pushes (Antworten, Likes, Kommentare, Bewertungen) + Chat-Pushes
  notificationsEnabledCommunity?: boolean | null;
  notificationsEnabledChat?: boolean | null;
  // ADDITIV (2026-09-29): E-Mails von gefolgten Einträgen (Nachricht an Follower)
  notificationsEnabledListingEmails?: boolean | null;
  permissions?: string[] | null;
  savedTournaments?: string[] | null;
  savedSeries?: string[] | null;
  savedClubs?: string[] | null;
  savedPubs?: string[] | null;
  /** Additiv (2026-09-16): Favoriten Liga/Verband */
  savedLeagues?: string[] | null;
  savedAssociations?: string[] | null;
  savedShops?: string[] | null;
  personalData?: PersonalData | null;
  createdAt?: number | null;
  updatedAt?: number | null;
  authToken?: string | null;
  /** Lieblingsspieler (denormalisierte ProPlayer) — Basis für personalisierte News-Pushes */
  favoriteProPlayers?: FavoriteProPlayer[] | null;
  [key: string]: any;
}

export interface FavoriteProPlayer {
  flashliveId?: string | null;
  name: string;
  slug?: string | null;
  image?: string | null;
}

export interface UpdateUserPayload {
  firstname?: string;
  lastname?: string;
  /** null = Telefonnummer löschen (Backend erlaubt String oder null, nie '') */
  phone?: string | null;
  addressLabel?: string;
  city?: string;
  postcode?: string;
  street?: string;
  latitude?: number;
  longitude?: number;
  country?: string;
  language?: string;
  notificationId?: string;
  username?: string;
  chatUsername?: string;
  profileImage?: string;
  notificationDistance?: number;
  notificationsEnabled?: boolean;
  notificationsEnabledFavourites?: boolean;
  notificationsEnabledNews?: boolean;
  notificationsEnabledOwner?: boolean;
  notificationsEnabledCommunity?: boolean;
  notificationsEnabledChat?: boolean;
  notificationsEnabledListingEmails?: boolean;
  favoriteProPlayers?: FavoriteProPlayer[];
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export interface SocialMediaLinks {
  instagram?: string | null;
  facebook?: string | null;
  twitter?: string | null;
  tiktok?: string | null;
  youtube?: string | null;
  // ADDITIV (2026-09-01): neue Plattformen — alle nullable (altes Backend liefert sie nicht)
  twitch?: string | null;
  discord?: string | null;
  threads?: string | null;
  snapchat?: string | null;
  kick?: string | null;
  bluesky?: string | null;
  spotify?: string | null;
  website?: string | null;
  [key: string]: any;
}

export interface DartSetup {
  id: string;
  image?: string | null;
  title?: string | null;
  subtitle?: string | null;
  description?: string | null;
  createdAt?: number | null;
  [key: string]: any;
}

export interface ClubMembershipInfo {
  id?: string | null;
  clubId?: string | null;
  clubName?: string | null;
  clubSlug?: string | null;
  clubImage?: string | null;
  status?: string | null;
  memberSince?: number | null;
  createdAt?: number | null;
  [key: string]: any;
}

/** Eigene Bestleistung (Label + Wert), max. 12 Stück. Alle Felder nullable. */
export interface CustomPersonalBest {
  id: string;
  label?: string | null;
  value?: string | number | null;
  [key: string]: any;
}

/** Persönliche Bestleistungen des Spielers. Alle Felder nullable. */
export interface PersonalBests {
  highestFinish?: number | null;
  bestAverage?: number | null;
  most180sSession?: number | null;
  shortestLeg?: number | null;
  customBests?: CustomPersonalBest[] | null;
  [key: string]: any;
}

/** Dartroom (Beschreibung + bis zu 8 Bilder als File-Namen). Alle Felder nullable. */
export interface DartRoom {
  description?: string | null;
  images?: string[] | null;
  [key: string]: any;
}

/** Turniererfolg (max. 30 Stück). Alle Felder außer id nullable. */
export interface ProfileAchievement {
  id: string;
  title?: string | null;
  tournamentName?: string | null;
  placement?: string | number | null;
  /** Unix-Timestamp in Millisekunden */
  date?: number | null;
  note?: string | null;
  /** Bilder als File-Namen (max. 6 pro Erfolg). */
  images?: string[] | null;
  [key: string]: any;
}

/** Eine Zeile der Aktivitäts-Punkte-Aufschlüsselung (nur count>0, vom Backend sortiert). */
export interface ActivityBreakdownEntry {
  key: string;
  count: number;
  points: number;
}

/** Punkte-Aufschlüsselung defensiv parsen (nur gültige Zeilen mit count>0, sonst []). */
const parseActivityBreakdown = (value: any): ActivityBreakdownEntry[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (entry: any) =>
        entry &&
        typeof entry === 'object' &&
        typeof entry.key === 'string' &&
        typeof entry.count === 'number' &&
        Number.isFinite(entry.count) &&
        entry.count > 0 &&
        typeof entry.points === 'number' &&
        Number.isFinite(entry.points)
    )
    .map((entry: any) => ({ key: entry.key, count: entry.count, points: entry.points }));
};

export interface PublicProfile {
  id?: string | null;
  username?: string | null;
  /** Anzeigename (max. 30 Zeichen, Emojis/Leerzeichen ok; leer = löschen → null). */
  displayName?: string | null;
  /** Aktivitäts-Badge (nur lesend; null = kein Badge oder altes Backend). */
  activityBadge?: 'bronze' | 'silver' | 'gold' | 'platinum' | null;
  /** Aktivitäts-Score (nur lesend; null bei altem Backend). */
  activityScore?: number | null;
  /** Punkte-Aufschlüsselung (nur lesend; leer bei altem Backend). */
  activityBreakdown?: ActivityBreakdownEntry[] | null;
  image?: string | null;
  isPublic?: boolean | null;
  headerImage?: string | null;
  bio?: string | null;
  /** Spitzname wie bei den Profis, z. B. "The Power" (max. 30 Zeichen). */
  nickname?: string | null;
  /** Walk-on Song als Freitext, z. B. "Queen – I Want It All" (max. 120 Zeichen). */
  walkOnSong?: string | null;
  /** Link zum Walk-on Song (nur http(s), max. 300 Zeichen; leer → null). 2026-09-28 */
  walkOnSongUrl?: string | null;
  socialMedia?: SocialMediaLinks | null;
  dartSetups?: DartSetup[] | null;
  clubMemberships?: ClubMembershipInfo[] | null;
  memberSince?: number | null;
  personalBests?: PersonalBests | null;
  dartRoom?: DartRoom | null;
  /** Allgemeine Bildergalerie: Datei-Ids wie dartRoom.images, max. 24. 2026-09-28 */
  gallery?: string[] | null;
  achievements?: ProfileAchievement[] | null;
  /** High Finishes 100–170 mit Wegen (2026-10-08, additiv) */
  highFinishes?: { finish: number; achieved: boolean; routes: { id?: string | null; route: string | null; image: string | null; createdAt?: number | null }[] }[] | null;
  // 🎯 Dart-Steckbrief (PUT /api/player/settings: undefined = unverändert,
  // null/leer = löschen) — ALLE Felder nullable, altes Backend liefert sie nicht.
  /** Jahr, seit dem Darts gespielt wird (z. B. 2015). */
  dartSince?: number | null;
  /** Dart-Art: steel = Steeldart, soft = E-Dart, both = beides. */
  dartType?: 'steel' | 'soft' | 'both' | null;
  /** Wurfhand: right = Rechtshand, left = Linkshand, both = beidhändig. */
  throwingHand?: 'right' | 'left' | 'both' | null;
  /** Dart-Gewicht in Gramm (1 Dezimale, z. B. 22.5). */
  dartWeight?: number | null;
  /** Lieblings-Double, z. B. "D16" (max. 20 Zeichen). */
  favoriteDouble?: string | null;
  /** Lieblings-Checkout, z. B. "170: T20 T20 Bull" (max. 60 Zeichen). */
  favoriteCheckout?: string | null;
  /** Liga/Verband (max. 100 Zeichen). */
  league?: string | null;
  /** Saison-Ziel (max. 120 Zeichen). */
  seasonGoal?: string | null;
  [key: string]: any;
}

export type FavouriteType = 'TOURNAMENT' | 'SERIES' | 'PUB' | 'CLUB' | 'SHOP' | 'LEAGUE' | 'ASSOCIATION';

export interface DartSetupPayload {
  image?: string | null;
  title?: string;
  subtitle?: string | null;
  description?: string | null;
}

export interface GeocodeResult {
  formattedAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  country?: string | null;
  [key: string]: any;
}

export interface FileUploadResponse {
  name?: string | null;
  data?: string | null;
}

const decodeJwtPayload = (token: string): any | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(padded);
    const json = decodeURIComponent(
      decoded
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const persistUser = (user: DartcloudUser) => {
  const merged: AuthUser = {
    id: user.id ?? user._id ?? null,
    email: user.personalData?.email ?? null,
    firstname: user.personalData?.firstname ?? null,
    lastname: user.personalData?.lastname ?? null,
    username: user.username ?? null,
  };
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify({ ...merged, ...user }));
};

export const getCurrentUser = async (): Promise<DartcloudUser> => {
  const response = await api.get<any>('/api/user/v2');
  const data = response.data;

  let user: DartcloudUser | null = null;
  let token: string | null = null;

  if (data && typeof data === 'object' && data.token && typeof data.token === 'string') {
    token = data.token as string;
    const payload = decodeJwtPayload(token as string);
    if (payload && typeof payload === 'object') {
      user = { ...payload, authToken: token };
    }
  } else if (data && typeof data === 'object') {
    user = data as DartcloudUser;
  }

  if (!user) {
    throw new Error(t('web.page.profile-page.profil-konnte-nicht-geladen-werden'));
  }

  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
  persistUser(user);
  return user;
};

export const updateUser = async (payload: UpdateUserPayload): Promise<DartcloudUser> => {
  const response = await api.post<any>('/api/user/update', payload);
  const data = response.data;

  if (data && typeof data === 'object' && data.token && typeof data.token === 'string') {
    const token = data.token as string;
    const decoded = decodeJwtPayload(token);
    if (decoded) {
      const user: DartcloudUser = { ...decoded, authToken: token };
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      persistUser(user);
      return user;
    }
  }

  if (data && typeof data === 'object') {
    persistUser(data);
    return data as DartcloudUser;
  }

  return await getCurrentUser();
};

export const changePassword = async (payload: ChangePasswordPayload): Promise<void> => {
  await api.post('/api/user/password/change', payload);
};

export const deleteAccount = async (): Promise<void> => {
  await api.post('/api/user/delete', {});
};

export const getPublicProfile = async (): Promise<PublicProfile | null> => {
  try {
    const response = await api.get<PublicProfile>('/api/player/settings');
    const profile = response.data ?? null;
    if (!profile || typeof profile !== 'object') return null;
    // activityBreakdown defensiv normalisieren (altes Backend → [])
    return { ...profile, activityBreakdown: parseActivityBreakdown(profile.activityBreakdown) };
  } catch {
    return null;
  }
};

export const updatePublicProfile = async (payload: Partial<PublicProfile>): Promise<PublicProfile> => {
  const response = await api.put<PublicProfile>('/api/player/settings', payload);
  const profile = response.data;
  if (!profile || typeof profile !== 'object') return profile;
  // activityBreakdown defensiv normalisieren (altes Backend → [])
  return { ...profile, activityBreakdown: parseActivityBreakdown(profile.activityBreakdown) };
};

export const togglePublicProfileVisibility = async (isPublic: boolean): Promise<PublicProfile> => {
  const response = await api.put<PublicProfile>('/api/player/visibility', { isPublic });
  return response.data;
};

/**
 * Header-Bild des öffentlichen Profils aktualisieren.
 * Erwartet das Bild als Base64 / Data-URL (Backend speichert es selbst als Datei).
 */
export const updateHeaderImage = async (
  imageData: string
): Promise<{ headerImage?: string | null }> => {
  const response = await api.put<any>('/api/player/header-image', { image: imageData });
  return response.data && typeof response.data === 'object' ? response.data : {};
};

/** Header-Bild des öffentlichen Profils entfernen. */
export const deleteHeaderImage = async (): Promise<{ headerImage?: string | null }> => {
  const response = await api.delete<any>('/api/player/header-image');
  return response.data && typeof response.data === 'object' ? response.data : {};
};

/** Neues Dart-Setup anlegen (Bild als Base64/Data-URL, Titel ist Pflicht). */
export const addDartSetup = async (payload: {
  image: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
}): Promise<DartSetup | null> => {
  const response = await api.post<any>('/api/player/dart-setup', payload);
  return response.data && typeof response.data === 'object' ? (response.data as DartSetup) : null;
};

/** Bestehendes Dart-Setup aktualisieren (nur übergebene Felder werden geändert). */
export const updateDartSetup = async (
  id: string,
  payload: DartSetupPayload
): Promise<DartSetup | null> => {
  const response = await api.put<any>(`/api/player/dart-setup/${encodeURIComponent(id)}`, payload);
  return response.data && typeof response.data === 'object' ? (response.data as DartSetup) : null;
};

/** Dart-Setup löschen. */
export const deleteDartSetup = async (id: string): Promise<void> => {
  await api.delete(`/api/player/dart-setup/${encodeURIComponent(id)}`);
};

// ============================================
// Favoriten
// ============================================

/** Eintrag als Favorit speichern. Gibt den aktualisierten User zurück (defensiv geparst). */
export const saveFavourite = async (
  id: string,
  type: FavouriteType
): Promise<DartcloudUser | null> => {
  const response = await api.post<any>('/api/user/favourites/save', { id, type });
  return response.data && typeof response.data === 'object'
    ? (response.data as DartcloudUser)
    : null;
};

/** Eintrag aus den Favoriten entfernen. Gibt den aktualisierten User zurück. */
export const removeFavourite = async (
  id: string,
  type: FavouriteType
): Promise<DartcloudUser | null> => {
  const response = await api.post<any>('/api/user/favourites/remove', { id, type });
  return response.data && typeof response.data === 'object'
    ? (response.data as DartcloudUser)
    : null;
};

const fetchSavedList = async (path: string): Promise<any[]> => {
  try {
    const res = await api.get<any>(path);
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.data?.data)) return res.data.data;
    return [];
  } catch (err) {
    console.error(`Fehler beim Laden der Favoriten (${path}):`, err);
    return [];
  }
};

export const getSavedTournaments = (): Promise<any[]> =>
  fetchSavedList('/api/user/saved/tournaments');
export const getSavedSeries = (): Promise<any[]> => fetchSavedList('/api/user/saved/series');
export const getSavedClubs = (): Promise<any[]> => fetchSavedList('/api/user/saved/clubs');
export const getSavedPubs = (): Promise<any[]> => fetchSavedList('/api/user/saved/pubs');
export const getSavedShops = (): Promise<any[]> => fetchSavedList('/api/user/saved/shops');
// Liga/Verband (2026-09-16) – altes Backend: 404 → fail-soft leere Liste
export const getSavedLeagues = (): Promise<any[]> => fetchSavedList('/api/user/saved/leagues');
export const getSavedAssociations = (): Promise<any[]> => fetchSavedList('/api/user/saved/associations');

export const geocodeAddress = async (address: string): Promise<GeocodeResult[]> => {
  if (!address || address.trim().length < 3) return [];
  try {
    const response = await api.post<GeocodeResult[]>('/api/geocoding/address', { address });
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
};

export const uploadFile = async (base64Data: string): Promise<FileUploadResponse> => {
  const response = await api.post<FileUploadResponse>('/api/file', { data: base64Data });
  return response.data;
};

export const getFileUrl = (fileName?: string | null): string | null => {
  if (!fileName) return null;
  if (fileName.startsWith('http://') || fileName.startsWith('https://')) return fileName;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');
  return `${base}/api/file/${fileName}`;
};

export const getOwnedTournaments = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/tournaments');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.tournaments ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der eigenen Turniere:', err);
    return [];
  }
};

export const getOwnedSeries = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/series');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.series ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der eigenen Turnierserien:', err);
    return [];
  }
};

export const getOwnedClubs = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/clubs');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.clubs ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der eigenen Vereine:', err);
    return [];
  }
};

export const getOwnedPubs = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/pubs');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.pubs ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der eigenen Spielstätten:', err);
    return [];
  }
};

export const getOwnedShops = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/shops');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.shops ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der eigenen Shops:', err);
    return [];
  }
};

export const getAdminTournaments = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/admin/tournaments');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.tournaments ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der Admin-Turniere:', err);
    return [];
  }
};

export const getAdminSeries = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/admin/series');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.series ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der Admin-Turnierserien:', err);
    return [];
  }
};

export const getAdminClubs = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/admin/clubs');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.clubs ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der Admin-Vereine:', err);
    return [];
  }
};

export const getAdminPubs = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/admin/pubs');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.pubs ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der Admin-Spielstätten:', err);
    return [];
  }
};

export const getAdminShops = async (): Promise<any[]> => {
  try {
    const res = await api.get<any>('/api/user/admin/shops');
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.shops ?? []);
  } catch (err) {
    console.error('Fehler beim Laden der Admin-Shops:', err);
    return [];
  }
};

// ---- Liga / Verband (2026-09-16): eigene + verwaltete Einträge ----
const fetchOwnedList = async (path: string, key: string, label: string): Promise<any[]> => {
  try {
    const res = await api.get<any>(path);
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.[key] ?? []);
  } catch (err) {
    console.error(`Fehler beim Laden der ${label}:`, err);
    return [];
  }
};
export const getOwnedLeagues = (): Promise<any[]> => fetchOwnedList('/api/user/leagues', 'leagues', t('web.service.user-service.eigenen-ligen'));
export const getOwnedAssociations = (): Promise<any[]> => fetchOwnedList('/api/user/associations', 'associations', t('web.service.user-service.eigenen-verbaende'));
export const getAdminLeagues = (): Promise<any[]> => fetchOwnedList('/api/user/admin/leagues', 'leagues', 'Admin-Ligen');
export const getAdminAssociations = (): Promise<any[]> => fetchOwnedList('/api/user/admin/associations', 'associations', t('web.service.user-service.admin-verbaende'));

const userService = {
  getCurrentUser,
  updateUser,
  changePassword,
  deleteAccount,
  getPublicProfile,
  updatePublicProfile,
  togglePublicProfileVisibility,
  updateHeaderImage,
  deleteHeaderImage,
  addDartSetup,
  updateDartSetup,
  deleteDartSetup,
  saveFavourite,
  removeFavourite,
  getSavedTournaments,
  getSavedSeries,
  getSavedClubs,
  getSavedPubs,
  getSavedShops,
  getSavedLeagues,
  getSavedAssociations,
  geocodeAddress,
  uploadFile,
  getFileUrl,
  getOwnedTournaments,
  getOwnedSeries,
  getOwnedClubs,
  getOwnedPubs,
  getOwnedShops,
  getAdminTournaments,
  getAdminSeries,
  getAdminClubs,
  getAdminPubs,
  getAdminShops,
  getOwnedLeagues,
  getOwnedAssociations,
  getAdminLeagues,
  getAdminAssociations,
};

export default userService;
